import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { HlsVideo } from '../components/cameras/HlsVideo';
import { apiList } from '../api/apiClient';
import { PropertyAssignmentPicker, type PropertyAssignmentValue } from '../components/access/PropertyAssignmentPicker';
import { DeviceActionButton } from '../components/access/DeviceVisuals';

const API_BASE = window.location.hostname.startsWith('shadow.')
  ? 'https://shadow.api.intrlock.io'
  : (process.env.REACT_APP_API_URL || 'https://api.intrlock.io');
function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('intrlock_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

interface TuringCamera {
  id: number;
  name: string;
  state: string;
  site_id: number;
  site_name?: string;
  manufacturer?: string;
  model?: string;
  serial_no?: string;
}

interface TuringSite {
  id: number;
  name: string;
  type: string;
  children?: TuringSite[];
}

export const CameraSystemDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [system, setSystem] = useState<any | null>(null);
  const [sites, setSites] = useState<TuringSite[]>([]);
  const [cameras, setCameras] = useState<TuringCamera[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [streamCameraId, setStreamCameraId] = useState<number | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  // Per-camera license status is learned only when the user requests a stream.
  // Stream URLs are billable/short-lived provider resources and must never be
  // created merely because a page was opened.
  type LicenseTag = 'core_ai' | 'no_license' | 'unknown' | 'checking';
  const [licenseByCamera, setLicenseByCamera] = useState<Record<number, LicenseTag>>({});
  const [cameraTab, setCameraTab] = useState<'streamable' | 'not_streamable'>('streamable');
  const [properties, setProperties] = useState<any[]>([]);
  const [cameraAssignment, setCameraAssignment] = useState<PropertyAssignmentValue>({ property_ids: [], primary_property_id: null });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [sysRes, sitesRes, camsRes] = await Promise.all([
        fetch(`${API_BASE}/integrations/turing/systems/${id}`,         { headers: authHeaders() }),
        fetch(`${API_BASE}/integrations/turing/systems/${id}/sites`,   { headers: authHeaders() }),
        fetch(`${API_BASE}/integrations/turing/systems/${id}/cameras?limit=50`, { headers: authHeaders() }),
      ]);
      if (sysRes.ok) {
        const nextSystem = await sysRes.json();
        setSystem(nextSystem);
        const ids = nextSystem.property_ids || nextSystem.linked_property_ids || (nextSystem.entity_id ? [nextSystem.entity_id] : []);
        setCameraAssignment({ property_ids: ids.map(String), primary_property_id: String(nextSystem.property_id || nextSystem.entity_id || ids[0] || '') || null });
      }
      if (sitesRes.ok) setSites((await sitesRes.json()).sites ?? []);
      if (camsRes.ok)  setCameras((await camsRes.json()).cameras ?? []);
    } catch (e) { console.error('Failed to load Turing system:', e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiList('properties').then(rows => setProperties(Array.isArray(rows) ? rows : [])).catch(() => setProperties([])); }, []);

  const sync = async () => {
    if (!id) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      if (!cameraAssignment.property_ids.length) throw new Error('Choose at least one property.');
      const r = await fetch(`${API_BASE}/integrations/turing/systems/${id}/sync-cameras`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(cameraAssignment),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Sync failed (${r.status})`);
      setSyncResult(`Synced ${cameraAssignment.property_ids.length} propert${cameraAssignment.property_ids.length === 1 ? 'y' : 'ies'} without duplicating cameras.`);
    } catch (e: any) {
      setSyncResult(`Error: ${String(e?.message ?? e).slice(0, 120)}`);
    } finally {
      setSyncing(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm('Remove this Turing system? Imported cameras stay; you can delete them separately.')) return;
    const r = await fetch(`${API_BASE}/integrations/turing/systems/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (r.ok) navigate('/cameras');
  };

  const playCamera = async (cameraId: number) => {
    setStreamCameraId(cameraId);
    setStreamUrl(null);
    setStreamError(null);
    try {
      const r = await fetch(`${API_BASE}/integrations/turing/systems/${id}/stream-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ camera_id: cameraId, resolution: 'sub' }),
      });
      const data = await r.json();
      if (!r.ok) { setLicenseByCamera((prev) => ({ ...prev, [cameraId]: 'unknown' })); setStreamError(`HTTP ${r.status}`); return; }
      if (data.error === 'license_required') {
        setLicenseByCamera((prev) => ({ ...prev, [cameraId]: 'no_license' }));
        setStreamError(data.message || 'Camera does not have a Core AI license.');
        return;
      }
      if (data.play_url) {
        setLicenseByCamera((prev) => ({ ...prev, [cameraId]: 'core_ai' }));
        setStreamUrl(data.play_url);
      } else {
        setLicenseByCamera((prev) => ({ ...prev, [cameraId]: 'unknown' }));
        setStreamError('No play URL returned.');
      }
    } catch (e: any) {
      setStreamError(String(e?.message ?? e).slice(0, 200));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  // Flatten site tree for display
  const flatSites: TuringSite[] = [];
  const walk = (s: TuringSite, depth = 0) => {
    flatSites.push({ ...s, name: `${'  '.repeat(depth)}${s.name}` });
    (s.children || []).forEach((c) => walk(c, depth + 1));
  };
  sites.forEach((s) => walk(s));

  // Group cameras by site
  const camerasBySite = new Map<number, TuringCamera[]>();
  for (const c of cameras) {
    if (!camerasBySite.has(c.site_id)) camerasBySite.set(c.site_id, []);
    camerasBySite.get(c.site_id)!.push(c);
  }

      return (
        <div className="space-y-6">
          <Card><PropertyAssignmentPicker properties={properties.map(p => ({ id: String(p.id || p.uuid), name: p.name }))}
            selectedIds={cameraAssignment.property_ids} primaryId={cameraAssignment.primary_property_id}
            onSave={async value => setCameraAssignment(value)} /></Card>
      <div className="flex items-center justify-between">
        <div>
          <button className="text-xs text-gray-500 hover:underline mb-1" onClick={() => navigate('/cameras')}>← Back to Cameras</button>
          <h1 className="text-2xl font-bold text-gray-900">Turing Vision — {system?.property_name ?? 'System'}</h1>
          <p className="text-gray-500 mt-1">
            {sites.length} site{sites.length === 1 ? '' : 's'} · {cameras.length} camera{cameras.length === 1 ? '' : 's'} ·
            attached to <span className="font-medium">{system?.property_name || system?.entity_id}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={sync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync to Property'}
          </Button>
          <Button variant="ghost" onClick={remove} className="!text-red-600 hover:!bg-red-50">Remove System</Button>
        </div>
      </div>

      {syncResult && <Card><div className="p-3 text-sm text-gray-700">{syncResult}</div></Card>}

      <Card padding={false}>
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold">Sites</h3>
          <p className="text-xs text-gray-400 mt-0.5">Turing-side site hierarchy. Cameras inherit their site's name as a label.</p>
        </div>
        <Table
          columns={[
            { key: 'name', header: 'Site Name', render: (s: any) => <span className="font-mono whitespace-pre">{s.name}</span> },
            { key: 'type', header: 'Type', render: (s: any) => <Badge color={s.type === 'site' ? 'blue' : 'gray'}>{s.type}</Badge> },
            { key: 'cams', header: 'Cameras', render: (s: any) => (camerasBySite.get(s.id) ?? []).length },
          ]}
          data={flatSites.map((s) => ({ ...s }))}
        />
      </Card>

      {(() => {
        const streamableCams = cameras.filter((c) => {
          const lic = licenseByCamera[c.id];
          return lic === 'core_ai' || lic === 'checking' || lic === undefined;
        });
        const notStreamableCams = cameras.filter((c) => {
          const lic = licenseByCamera[c.id];
          return lic === 'no_license' || lic === 'unknown';
        });
        const shown = cameraTab === 'streamable' ? streamableCams : notStreamableCams;
        const tabBtn = (key: 'streamable' | 'not_streamable', label: string, count: number) => (
          <button
            type="button"
            onClick={() => setCameraTab(key)}
            className={`px-3 py-1.5 text-sm rounded-md transition ${
              cameraTab === key
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label} <span className={`ml-1 text-xs ${cameraTab === key ? 'opacity-80' : 'text-gray-500'}`}>({count})</span>
          </button>
        );
        return (
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="font-semibold">Cameras</h3>
                <div className="flex gap-2">
                  {tabBtn('streamable', 'Streamable', streamableCams.length)}
                  {tabBtn('not_streamable', 'Not Streamable', notStreamableCams.length)}
                </div>
              </div>
              <p className="text-xs text-gray-400">
                {cameraTab === 'streamable'
                  ? 'Click a camera to check its license and open the live stream. No provider stream is requested until you click.'
                  : 'Essential-license cameras — streaming requires a Core AI upgrade in the Turing portal.'}
              </p>
            </div>
            <Table
              onRowClick={(c: any) => {
                if (licenseByCamera[c.id] === 'no_license') return;
                playCamera(c.id);
              }}
              columns={[
                { key: 'name', header: 'Name', render: (c: any) => {
                  const lic = licenseByCamera[c.id];
                  const dim = lic === 'no_license';
                  return (
                    <span className={`font-medium inline-flex items-center gap-2 ${dim ? 'text-gray-400' : 'text-gray-900'}`}>
                      {dim
                        ? <span title="Essential license — streaming requires Core AI">🔒</span>
                        : lic === 'core_ai' ? <span className="text-green-600">▶</span>
                        : <span className="text-gray-300">▶</span>}
                      {c.name}
                    </span>
                  );
                } },
                { key: 'site', header: 'Site', render: (c: any) => sites.flatMap((s) => [s, ...(s.children || [])]).find((s) => s.id === c.site_id)?.name ?? c.site_id },
                { key: 'state', header: 'State', render: (c: any) => (
                  <Badge color={c.state === 'running' ? 'green' : 'red'}>{c.state}</Badge>
                ) },
                { key: 'license', header: 'License', render: (c: any) => {
                  const lic = licenseByCamera[c.id];
                  if (lic === 'core_ai') return <Badge color="green">Core AI</Badge>;
                  if (lic === 'no_license') return <Badge color="gray">Essential license</Badge>;
                  if (lic === 'checking') return <span className="text-xs text-gray-400 italic">checking…</span>;
                  return <span className="text-xs text-gray-400">Check on play</span>;
                } },
                { key: 'model', header: 'Model', render: (c: any) => <span className="text-xs text-gray-500">{c.manufacturer || ''} {c.model || ''}</span> },
                { key: 'control', header: 'Device Control', render: (c: any) => (
                  <DeviceActionButton
                    disabled={licenseByCamera[c.id] === 'no_license'}
                    loading={licenseByCamera[c.id] === 'checking'}
                    loadingLabel="Checking…"
                    onClick={(event) => { event.stopPropagation(); playCamera(c.id); }}
                  >
                    View Stream
                  </DeviceActionButton>
                ) },
              ]}
              data={shown.map((c) => ({ ...c, id: c.id }))}
              emptyMessage={cameraTab === 'streamable' ? 'No cameras are available.' : 'No Essential-only cameras have been detected.'}
            />
          </Card>
        );
      })()}

      {/* HLS preview overlay */}
      {streamCameraId !== null && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setStreamCameraId(null); setStreamUrl(null); setStreamError(null); }}>
          <div className="bg-black rounded-lg w-full max-w-3xl aspect-video relative" onClick={(e) => e.stopPropagation()}>
            {streamError ? (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center">{streamError}</div>
            ) : streamUrl ? (
              <HlsVideo
                src={streamUrl}
                className="w-full h-full"
                onRefreshStreamUrl={async () => {
                  if (!id || streamCameraId === null) return null;
                  try {
                    const r = await fetch(`${API_BASE}/integrations/turing/systems/${id}/stream-url`, {
                      method: 'POST',
                      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({ camera_id: streamCameraId, resolution: 'sub' }),
                    });
                    const data = await r.json();
                    return data.play_url ?? null;
                  } catch { return null; }
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Fetching stream URL…</div>
            )}
            <button className="absolute top-2 right-2 bg-white/10 text-white rounded px-2 py-1 text-xs" onClick={() => { setStreamCameraId(null); setStreamUrl(null); setStreamError(null); }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};
